import { z } from 'zod/v4-mini';

import { useLocalizationStore } from '$/components/core/Localization/hooks/useLocalizationStore';
import { config } from '$/config';
import { backendEndpoints } from '$/services/endpoints';
import { fetcher } from '$/services/fetcher';
import {
  type MaterialFilterOption,
  type SpectrumWidgetRequest,
} from '$/services/usecases/materials';
import { type FilterType } from '$/services/usecases/materials/mapper/materials';

const FilterSearchSchema = z.object({
  '@search.facets': z.record(
    z.string(),
    z.array(z.object({ value: z.string(), count: z.number() })),
  ),
});

const filterColorOrder = [...config.filter.colors.keys()];

type Payload = {
  brightnessFilter?: number[];
  filter?: MaterialFilterOption[];
  facets: string[];
  signal?: AbortSignal;
  countryOverwrite?: string;
  disableAdminFilter?: boolean;
  spectrumWidget?: SpectrumWidgetRequest;
  includeCredentials?: boolean;
};

export const getMaterialSearchFilter = async (payload: Payload) => {
  const {
    filter,
    facets,
    signal,
    countryOverwrite,
    disableAdminFilter,
    spectrumWidget,
    includeCredentials,
  } = payload;
  const country = useLocalizationStore.getState().country;

  const response = await fetcher(backendEndpoints.MATERIAL_FILTER_URL, {
    signal,
    body: {
      top: 0,
      facets,
      filter,
      country: countryOverwrite ?? country,
      disableAdminFilter,
      spectrumWidget,
    },
    method: 'POST',
    parser: (data) => FilterSearchSchema.parse(data),
    includeCredentials: includeCredentials ?? true,
  });

  const transformed = Object.entries(response.response['@search.facets']).map(
    ([key, value]) => ({
      searchField: key,
      items: value.map((item) => ({
        option: item.value,
        count: item.count,
        payload: undefined as undefined | string,
      })),
      isMultiSelect: ['colorRanges'].includes(key),
      isAdditional: ['-'].includes(key),
    }),
  );

  transformed.forEach((item) => {
    if (item.searchField === 'colorRanges') {
      item.items.sort(
        (a, b) =>
          filterColorOrder.indexOf(a.option) -
          filterColorOrder.indexOf(b.option),
      );
      item.items.forEach(
        (item) => (item.payload = config.filter.colors.get(item.option)),
      );
    }
  });

  return transformed;
};

export const getMaterialSearchFilterByType = async (
  opt: {
    filter?: MaterialFilterOption[];
    type?: FilterType | 'admin_collections';
    disableAdminFilter?: boolean;
    spectrumWidget?: SpectrumWidgetRequest;
    includeCredentials?: boolean;
  },
  signal: AbortSignal,
) => {
  const getFilter = (payload: Payload) =>
    getMaterialSearchFilter({
      ...payload,
      spectrumWidget: opt.spectrumWidget,
      includeCredentials: opt.includeCredentials,
    });
  let filter = [];
  switch (opt.type) {
    case 'color_tint': {
      filter = [
        getFilter({
          signal: signal,
          facets: ['producer,count:0'],
          filter: [{ option: 'type', value: [opt.type] }],
        }),
        getFilter({
          signal: signal,
          facets: ['collection,count:0'],
          filter: [
            { option: 'type', value: [opt.type] },
            ...(opt.filter
              ? opt.filter.filter((f) => f.option === 'producer')
              : []),
          ],
        }),
        getFilter({
          signal: signal,
          facets: ['colorRanges,count:0'],
          filter: [
            { option: 'type', value: [opt.type] },
            ...(opt.filter
              ? opt.filter.filter((f) =>
                  ['producer', 'collection'].includes(f.option),
                )
              : []),
          ],
        }),
      ];
      break;
    }
    case 'wall_texture': {
      filter = [
        getFilter({
          signal: signal,
          facets: ['producer,count:0'],
          filter: [{ option: 'type', value: [opt.type] }],
        }),
        getFilter({
          signal: signal,
          facets: ['productGroup,count:0'],
          filter: [
            {
              option: 'type',
              value: [opt.type],
            },
            ...(opt.filter
              ? opt.filter.filter((f) => ['producer'].includes(f.option))
              : []),
          ],
        }),
        getFilter({
          signal: signal,
          facets: ['collection,count:0'],
          filter: [
            { option: 'type', value: [opt.type] },
            ...(opt.filter
              ? opt.filter.filter((f) =>
                  ['producer', 'productGroup'].includes(f.option),
                )
              : []),
          ],
        }),
        getFilter({
          signal: signal,
          facets: ['colorRanges,count:0'],
          filter: [
            { option: 'type', value: [opt.type] },
            ...(opt.filter
              ? opt.filter.filter((f) =>
                  ['producer', 'collection', 'productGroup'].includes(f.option),
                )
              : []),
          ],
        }),
      ];
      break;
    }
    case 'floor_texture': {
      filter = [
        getFilter({
          signal: signal,
          facets: ['producer,count:0'],
          filter: [{ option: 'type', value: [opt.type] }],
        }),
        getFilter({
          signal: signal,
          facets: ['productGroup,count:0'],
          filter: [
            {
              option: 'type',
              value: [opt.type],
            },
            ...(opt.filter
              ? opt.filter.filter((f) => ['producer'].includes(f.option))
              : []),
          ],
        }),
        getFilter({
          signal: signal,
          facets: ['collection,count:0'],
          filter: [
            { option: 'type', value: [opt.type] },
            ...(opt.filter
              ? opt.filter.filter((f) =>
                  ['producer', 'productGroup'].includes(f.option),
                )
              : []),
          ],
        }),
        getFilter({
          signal: signal,
          facets: ['colorRanges,count:0'],
          filter: [
            { option: 'type', value: [opt.type] },
            ...(opt.filter
              ? opt.filter.filter((f) =>
                  ['producer', 'collection', 'productGroup'].includes(f.option),
                )
              : []),
          ],
        }),
      ];
      break;
    }
    case 'facade_texture': {
      filter = [
        getFilter({
          signal: signal,
          facets: ['producer,count:0'],
          filter: [{ option: 'type', value: [opt.type] }],
        }),
        getFilter({
          signal: signal,
          facets: ['productGroup,count:0'],
          filter: [
            {
              option: 'type',
              value: [opt.type],
            },
            ...(opt.filter
              ? opt.filter.filter((f) => ['producer'].includes(f.option))
              : []),
          ],
        }),
        getFilter({
          signal: signal,
          facets: ['collection,count:0'],
          filter: [
            { option: 'type', value: [opt.type] },
            ...(opt.filter
              ? opt.filter.filter((f) =>
                  ['producer', 'productGroup'].includes(f.option),
                )
              : []),
          ],
        }),
        getFilter({
          signal: signal,
          facets: ['colorRanges,count:0'],
          filter: [
            { option: 'type', value: [opt.type] },
            ...(opt.filter
              ? opt.filter.filter((f) =>
                  ['producer', 'collection', 'productGroup'].includes(f.option),
                )
              : []),
          ],
        }),
      ];
      break;
    }
    case 'admin_collections': {
      filter = [
        getFilter({
          signal: signal,
          facets: ['producerCollectionCombi,count:0'],
          disableAdminFilter: true,
        }),
      ];
      break;
    }
    default: {
      filter = [
        getFilter({
          signal: signal,
          facets: ['producer,count:0'],
          disableAdminFilter: opt.disableAdminFilter,
        }),
        getFilter({
          signal: signal,
          facets: ['collection,count:0'],
          filter: opt.filter?.filter((f) => f.option === 'producer'),
          disableAdminFilter: opt.disableAdminFilter,
        }),
        getFilter({
          signal: signal,
          facets: ['colorRanges,count:0'],
          filter: opt.filter?.filter((f) =>
            ['producer', 'collection'].includes(f.option),
          ),
          disableAdminFilter: opt.disableAdminFilter,
        }),
      ];
      break;
    }
  }

  const filterResponse = (await Promise.allSettled(filter)).flatMap((r) =>
    r.status === 'fulfilled' && r.value ? r.value : [],
  );

  return filterResponse;
};
